import { produce } from 'immer';
import { cloneDeep, get, set } from 'lodash';
import type { Primitive, Required } from 'utility-types';

import { GADimension, TrackingCategory } from '@swf/core/enums';
import type { TrackingContextValue } from '@swf/core/providers/TrackingContext';
import { getState } from '@swf/core/store';
import type { CasinoGame, PlayType } from '@swf/core/types/CasinoGame';
import type { CasinoRace } from '@swf/core/types/CasinoRaces';
import { customEvent } from '@swf/core/utils';
import { buttonClick } from '@swf/generated/analytics/buttonClick';
import type { Entity, CasinoGame as GameEntity } from '@swf/generated/analytics/entities';
import { casinoGame, casinoRace } from '@swf/generated/analytics/entities';

import { GAEvent } from './utils';

export const gameEntity = (game: CasinoGame, index?: number): Entity<GameEntity> =>
  casinoGame({
    gameTags: game.tags,
    gameId: game.gametype,
    gameName: game.name,
    gamePosition: typeof index === 'number' ? index + 1 : undefined,
    jackpotCurrency: game.jackpot?.currency as GameEntity['jackpotCurrency'],
    jackpotValue: game.jackpot?.value,
    variantId: game.variant
  });

export const raceEntity = ({ id, name, numOfPlayers, status }: CasinoRace) =>
  casinoRace({
    numberOfPlayers: numOfPlayers,
    raceId: id,
    raceName: name,
    raceStatus: status
  });

export const gameDimensions = (
  game: CasinoGame,
  index?: number,
  totalGames = 1
): Record<string, string[] | Primitive> | undefined => ({
  [GADimension.UIElementPosition]: typeof index === 'number' ? index + 1 : undefined,
  [GADimension.GameName]: game.name,
  [GADimension.GameCode]: game.gametype,
  [GADimension.GameTags]: game.tags,
  [GADimension.GameJackpot]: game.jackpot ? game.jackpot.value / 100 : null,
  [GADimension.GamesTotal]: totalGames,
  [GADimension.GameVariantID]: game.variant
});

const gameEntities = (context: TrackingContextValue, game: CasinoGame, index?: number) =>
  produce(context, (draft) => {
    draft.entities = draft.entities || [];
    draft.entities.push(gameEntity(game, index));
  });

interface LaunchParams {
  action?: string;
  game: CasinoGame;
  index?: number;
  totalGames?: number;
  buttonName?: string;
  playType?: PlayType;
}

export const casinoGameLaunched = (
  context: TrackingContextValue,
  {
    game,
    index,
    totalGames = 1,
    buttonName = 'Play',
    playType,
    action = 'Game Launched'
  }: LaunchParams
) => {
  const widgetInfo = get(getState(), context?.widget?.widgetId || '');
  const realName = widgetInfo?.games?.title || context.widget?.widgetName; // TODO: move to widget trackingReducer
  const newContext = set(cloneDeep(context), ['widget', 'widgetName'], realName);

  GAEvent(
    { action, label: game.name, value: game.gametype },
    { ...newContext, category: TrackingCategory.Casino },
    { [GADimension.GamePlayMode]: playType, ...gameDimensions(game, index, totalGames) }
  );

  customEvent({
    tag: 'WF_ClickToPlay',
    data: { widgetName: context.widget?.widgetName, ...game }
  });

  buttonClick({ buttonName }, gameEntities(context, game, index));
};

export const trackTrailer = (
  context: TrackingContextValue,
  { action, game, index, totalGames = 1, buttonName = 'Trailer' }: Required<LaunchParams, 'action'>
) => {
  GAEvent(
    { action, label: game.name },
    { ...context, category: TrackingCategory.Casino, ...gameDimensions(game, index) }
  );
};

export const trackInfo = (
  context: TrackingContextValue,
  game: CasinoGame,
  index = 0,
  totalGames = 1
) => {
  GAEvent(
    { action: 'Button Pressed', label: 'Game Info' },
    { ...context, category: TrackingCategory.Casino, ...gameDimensions(game, index, totalGames) }
  );

  buttonClick({ buttonName: 'Game Info' }, gameEntities(context, game, index));
};
